import {DSSpinner, Col, DSFieldLabel, DSIconGhost, Row, Text} from "@cdx/ds";
import {Card, CardId} from "../../../../cdx-models/Card";
import {User, UserId} from "../../../../cdx-models/User";
import {api} from "../../../../lib/api";
import {checkIfPresent} from "../../../../lib/mate/mate-utils";
import {convoStyles as styles} from "./convo.css";
import {useState} from "react";
import {union} from "../../../../lib/collection-utils";
import {
  lexicalSerializer,
  useCdxEditorState,
} from "../../../../components/RichTextarea/Lexical/LexicalRichTextProvider";
import {createPersistStore} from "../../../../lib/hooks/usePersistStore";
import {Resolvable, ResolvableId} from "../../../../cdx-models/Resolvable";
import useMutation from "../../../../lib/hooks/useMutation";
import Hint from "../../../../components/Hint";
import {useShouldShowHintKey} from "../../../hints/HintManager";
import ParticipantList from "./NewThreadParticipants";
import CommentForm from "./CommentForm";
import {ConvoBag} from "./convo-utils";

const infoByType = {
  comment: {
    description: (
      <>
        <b>Mention co-workers</b> to add them to this conversation.
      </>
    ),
    inputPlaceholder:
      "e.g. hey @alex, I like your idea! Will send you some screenshots later today.",
    heading: "Create new comment thread",
    inputLabel: "Your comment",
  },
  block: {
    description: (
      <>
        <b>Notify co-workers that you&#8217;re stuck</b> and mention anyone who might be able to
        help you get unstuck.
      </>
    ),
    inputPlaceholder: "e.g. @manu I can't implement this without proper specs.",
    inputLabel: "Submit a message to block this card.",
    heading: "Block this card",
  },
  review: {
    description: (
      <>
        <b>Mark card as to be reviewed</b>. You can mention co-workers to invite them to give
        feedback.
      </>
    ),
    inputPlaceholder: "e.g. @daniel I think I'm done here, can you have a look?",
    inputLabel: "Submit a message to start the review.",
    heading: "Start a review",
  },
} as const;

const threadCreationPersistStore = createPersistStore<{card: Card; type: Resolvable["context"]}>({
  elementToKey: ({card, type}) => {
    const id = card.$meta.get("cardId", false);
    return id !== false ? `create-resolvable-${id}-${type}` : null;
  },
});

const DiscordHint = () => {
  const [shouldShow, dismiss] = useShouldShowHintKey("conversations.discordParticipant");
  if (!shouldShow) return null;
  return (
    <Hint onLight action={{label: "Understood", onClick: dismiss}}>
      Adding the Discord user to the conversation will forward messages via Discord DMs. Their
      replies will be included in the thread.
    </Hint>
  );
};

type InnerNewThreadProps = NewThreadProps & {reviewGuardians: User[]};
const InnerNewThread = (props: InnerNewThreadProps) => {
  const {type, bag, reviewGuardians, onCreateNewThread, autoFocus, onReceiveFile} = props;
  const {card, root} = bag;
  const [addedParticipantIds, setAddedParticipantIds] = useState<Set<UserId>>(() =>
    reviewGuardians.length ? new Set(reviewGuardians.map((g) => g.id as UserId)) : new Set()
  );

  const onParticipantIds = (rawNextSet: Set<UserId>) => {
    let nextSet = rawNextSet;
    if (reviewGuardians.length) {
      nextSet = new Set(rawNextSet);
      reviewGuardians.forEach((g) => nextSet.add(g.id as UserId));
    }
    if (
      nextSet.size !== addedParticipantIds.size ||
      union([nextSet, addedParticipantIds]).size !== nextSet.size
    ) {
      setAddedParticipantIds(nextSet);
    }
  };

  const getContentKey = () => {
    const id = card.$meta.get("cardId", null);
    return id ? `${type}-${id}` : null;
  };

  const [isEmpty, setIsEmpty] = useState(true);
  const contentField = useCdxEditorState({
    initialContent: () =>
      lexicalSerializer.fromJSON(threadCreationPersistStore.getStoredValue({type, card})) || "",
    contentKey: getContentKey(),
    onChangeListeners: {
      allUserIds: onParticipantIds,
      isEmpty: setIsEmpty,
    },
  });
  threadCreationPersistStore.usePersistValues({type, card}, () => {
    if (!isEmpty) {
      return lexicalSerializer.toJSON(contentField);
    }
  });

  const [doCreate] = useMutation("resolvables", "create");

  const handleFormSubmit = () => {
    return doCreate({
      cardId: card.cardId as CardId,
      context: type,
      content: contentField.getText(),
      userId: root.loggedInUser.id,
    }).then(({id}: {id: ResolvableId}) => {
      threadCreationPersistStore.clearStoredValue({type, card});
      onCreateNewThread();
    });
  };

  const typeInfo = infoByType[type] || infoByType.comment;
  return (
    <Col
      rounded={12}
      className={styles.themes[type]}
      colorTheme="gray25"
      bg="foreground"
      pa="4px"
      sp="16px"
    >
      <Row className={styles.mainAsBg} minHeight="40px" align="center" px="12px" rounded={8}>
        <Text type="label14" as="h2" color="primary">
          {typeInfo.heading}
        </Text>
      </Row>
      <Col px="12px" sp="16px" pb="12px">
        {card.deck ? (
          <Col sp="12px">
            <Text type="prose14" color="primary">
              {typeInfo.description}
            </Text>
            {card.meta.discord && <DiscordHint />}
            <ParticipantList
              addedParticipantIds={addedParticipantIds}
              onParticipantsAdd={({userId, userName}) =>
                contentField.addUser({id: userId, name: userName})
              }
              onParticipantsRemove={({userId, userName}) =>
                contentField.removeUser({id: userId, name: userName})
              }
              bag={bag}
              reviewGuardians={reviewGuardians}
            />
          </Col>
        ) : (
          <Text type="prose14" color="primary">
            You can't mention anyone as{" "}
            <b>
              this is a <DSIconGhost size={16} inline /> Private Card
            </b>
            . Put it into a Deck to share it.
          </Text>
        )}
        <Col>
          <DSFieldLabel>{typeInfo.inputLabel}</DSFieldLabel>
          <CommentForm
            onSubmit={handleFormSubmit}
            content={contentField}
            isEmpty={isEmpty}
            placeholder={typeInfo.inputPlaceholder}
            onReceiveFile={onReceiveFile}
            autoFocus={autoFocus}
            card={card}
          />
        </Col>
      </Col>
    </Col>
  );
};

type NewThreadProps = {
  type: "comment" | "review" | "block";
  bag: ConvoBag;
  onCreateNewThread: () => void;
  onReceiveFile: (f: any) => any;
  autoFocus?: boolean;
};

const NewThread = (props: NewThreadProps) => {
  const {type, bag} = props;
  const {isLoaded, result: reviewGuardians} = checkIfPresent(
    () =>
      type === "review" && bag.card.deck?.hasGuardians
        ? bag.card.deck.guardians.map((g) => g.user)
        : [],
    api
  );
  return isLoaded ? (
    <InnerNewThread reviewGuardians={reviewGuardians} {...props} />
  ) : (
    <DSSpinner size={24} />
  );
};

export default NewThread;
